/**
 * Clients service (live from W3.M6).
 *
 * Live contract, as implemented in `@backend`:
 *
 *   GET    /clients?q=&risk=&monitoring=&sort=&limit=&offset=  → Page<ClientOut>
 *   GET    /clients/summary                                    → ClientSummary
 *   GET    /clients/:id                                        → ClientOut
 *   POST   /clients   { name, contact_email, industry }        → ClientOut
 *   PATCH  /clients/:id                                        → ClientOut
 *   DELETE /clients/:id                                        → 204
 *
 *   GET    /clients/:id/people      → who has been given access to the company
 *   GET    /clients/:id/documents   → the files on its trademarks
 *
 * The company feed on the clients page is the case timelines' own
 * (`oppositions.service.activity`); until 2026-09-30 it was the mockup's
 * fixture, printed as live under every real firm.
 */
import { apiResolve, apiEcho, http, dedupe } from '@/api'
import { isLive } from '@/api/config'
import { createClient, createClientList } from '@/entities/client'
import type { Client } from '@/entities/client'
import type { RiskLevel } from '@/entities/raw'
import clients from '@/mock/clients-page.json'
import type { Filters, ServiceOptions } from './types'

const LIVE = () => isLive('clients')

/** A page of anything, as the API returns it. */
export interface Paged<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

/** What the API sends. */
interface ClientSummaryOut {
  total: number
  new_this_quarter: number
  portfolios: number
  avg_per_client: number
  high_risk: number
  jurisdictions: number
  by_jurisdiction: Record<string, number>
  by_monitoring: Record<string, number>
  by_risk: Record<string, number>
}

/** What the KPI row reads. Named as the UI names things, not as SQL does. */
export interface ClientSummary {
  total: number
  new: number
  portfolios: number
  avgPerClient: number
  highRisk: number
  jurisdictions: number
  byJurisdiction: Record<string, number>
  byMonitoring: Record<string, number>
  byRisk: Record<string, number>
}

export const EMPTY_SUMMARY: ClientSummary = {
  total: 0,
  new: 0,
  portfolios: 0,
  avgPerClient: 0,
  highRisk: 0,
  jurisdictions: 0,
  byJurisdiction: {},
  byMonitoring: {},
  byRisk: {},
}

export function summaryFromApi(row: ClientSummaryOut): ClientSummary {
  return {
    total: row.total,
    new: row.new_this_quarter,
    portfolios: row.portfolios,
    avgPerClient: row.avg_per_client,
    highRisk: row.high_risk,
    jurisdictions: row.jurisdictions,
    byJurisdiction: row.by_jurisdiction ?? {},
    byMonitoring: row.by_monitoring ?? {},
    byRisk: row.by_risk ?? {},
  }
}

interface ClientOut {
  id: number
  name: string
  contact_email: string | null
  industry: string | null
  monitoring_status: string
  risk_level: string | null
  trademark_count: number
  open_case_count: number
  jurisdictions: string[]
  lawyers: { id: number; display_name: string }[]
  created_at: string
  updated_at: string
}

/** The stored fields of one client, as `GET /clients/:id` returns them. */
export interface ClientProfile {
  id: number
  name: string
  trading_name: string | null
  legal_form: string | null
  registration_number: string | null
  founded_year: number | null
  industry: string | null
  company_size: string | null
  website: string | null
  country: string | null
  primary_contact_name: string | null
  contact_email: string | null
  notes: string | null
  trademark_count: number
  open_case_count: number
}

const EMPTY_PROFILE: Omit<ClientProfile, 'id' | 'name'> = {
  trading_name: null,
  legal_form: null,
  registration_number: null,
  founded_year: null,
  industry: null,
  company_size: null,
  website: null,
  country: null,
  primary_contact_name: null,
  contact_email: null,
  notes: null,
  trademark_count: 0,
  open_case_count: 0,
}

/** A brand as the client dossier needs it — a subset of the brands endpoint. */
export interface ClientBrand {
  id: number
  title: string
  mark_type: string | null
  country: string | null
  registration_number: string | null
  registration_date: string | null
  logo_uri: string | null
  nice_entries: { class_number: number }[]
  scopes: { feed_id: number; active: boolean; last_scan_at?: string | null }[]
}

/** Someone given access to one company (GET /clients/:id/people). */
export interface ClientPerson {
  id: number
  display_name: string
  email: string
  user_kind: 'firm_manager' | 'client_viewer' | string
  lawyer_role: string | null
  active: boolean
  /** The trademarks they are narrowed to; null for the whole company. */
  brand_ids: number[] | null
}

/** A trademark's file, as its company's dossier lists it. */
export interface ClientDocument {
  id: number
  brand_id: number
  brand_title: string
  kind: string
  file_name: string
  content_type: string
  size_bytes: number
  created_at: string
}

/** The database says 'medium'; the design system's class is `tag--med`. */
const RISK: Record<string, RiskLevel> = { high: 'high', medium: 'med', low: 'low' }

function initialsOf(name: string): string {
  return (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toUpperCase()
}

/**
 * ClientOut → the `Client` the pages render.
 *
 * The fixtures carried a few fields the schema has no home for — a logo file,
 * a commercial `tier`, a `coverage` percentage. They are left at their entity
 * defaults rather than invented here: a made-up 92% coverage on a real client
 * row is worse than an absent one, because it looks authoritative.
 */
export function clientFromApi(row: ClientOut): Client {
  const lawyer = row.lawyers?.[0]
  return createClient({
    id: row.id,
    name: row.name,
    initials: initialsOf(row.name),
    industry: row.industry ?? '',
    contact: row.contact_email ?? '',
    primaryContactEmail: row.contact_email ?? '',
    trademarks: row.trademark_count,
    activeCases: row.open_case_count,
    /* Null stays null. `risk_level` is a rollup the product does not yet
     * compute for every client, and reading an absent one as "Low risk" is a
     * claim rather than a gap — on a screen a lawyer uses to decide where to
     * look first. */
    risk: row.risk_level ? (RISK[row.risk_level] ?? null) : null,
    jurisCodes: row.jurisdictions ?? [],
    lawyer: lawyer
      ? { name: lawyer.display_name, initials: initialsOf(lawyer.display_name) }
      : null,
    monitoringStatus: row.monitoring_status,
    lawyers: row.lawyers ?? [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  })
}

/** UI filter state → the query string the API understands. */
function toParams(filters: Filters = {}): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  if (filters.query) params.q = filters.query
  /* The UI's 'med' is the database's 'medium'; sending the UI spelling would
   * silently return an unfiltered list, which reads as "the filter is broken"
   * rather than "no matches". */
  if (filters.risk) params.risk = filters.risk === 'med' ? 'medium' : filters.risk
  if (filters.monitoring) params.monitoring = filters.monitoring
  if (filters.jurisdiction) params.jurisdiction = filters.jurisdiction
  if (filters.sort) params.sort = filters.sort
  if (filters.limit != null) params.limit = filters.limit
  if (filters.offset != null) params.offset = filters.offset
  return params
}

function applyMockFilters(rows: any[], { query, risk }: Filters = {}) {
  let out = rows
  if (query) {
    const q = query.toLowerCase()
    out = out.filter((c) => c.name.toLowerCase().includes(q))
  }
  if (risk) out = out.filter((c) => c.risk === risk)
  return out
}

export default {
  async list(filters: Filters = {}, { signal }: ServiceOptions = {}): Promise<Paged<Client>> {
    if (!LIVE()) {
      const key = `GET /clients ${JSON.stringify(filters)}`
      return dedupe(key, async () => {
        const rows = applyMockFilters(clients.rows, filters)
        const items = await apiResolve(createClientList(rows), {
          signal,
          path: '/clients',
          method: 'GET',
        })
        return { items, total: items.length, limit: items.length, offset: 0 }
      })
    }
    const page: Paged<ClientOut> = await http.get('/clients', { params: toParams(filters), signal })
    return { ...page, items: (page.items ?? []).map(clientFromApi) }
  },

  async summary({ signal }: ServiceOptions = {}): Promise<ClientSummary> {
    if (!LIVE()) {
      return apiResolve({ ...EMPTY_SUMMARY, ...clients.kpis } as ClientSummary, {
        signal,
        path: '/clients/summary',
      })
    }
    /* Adapted rather than passed through: the KPI row would otherwise read
     * `avg_per_client` in one place and `avgPerClient` in another, and a
     * mismatch renders as a card with a label and no number — which is
     * exactly what happened the first time this went live. */
    return summaryFromApi(await http.get('/clients/summary', { signal }))
  },

  /** Who has been given access to this company: managers and client users. */
  async people(id: string | number, { signal }: ServiceOptions = {}): Promise<ClientPerson[]> {
    if (!LIVE()) return apiResolve([], { signal, path: `/clients/${id}/people` })
    return (await http.get(`/clients/${id}/people`, { signal })) ?? []
  },

  /** The files on this company's trademarks, newest first. */
  async documents(id: string | number, { signal }: ServiceOptions = {}): Promise<ClientDocument[]> {
    if (!LIVE()) return apiResolve([], { signal, path: `/clients/${id}/documents` })
    return (await http.get(`/clients/${id}/documents`, { signal })) ?? []
  },

  async get(id: string | number, { signal }: ServiceOptions = {}): Promise<Client | null> {
    if (!LIVE()) {
      const row = clients.rows.find((c) => String(c.id) === String(id))
      return apiResolve(row ? createClient(row) : null, {
        signal,
        path: `/clients/${id}`,
        delayMs: 80,
      })
    }
    const data = await http.get(`/clients/${id}`, { signal })
    return data ? clientFromApi(data) : null
  },

  /**
   * The stored record as it is, for the edit form. `get` maps a row into the
   * list's entity, which keeps what the cards show and drops the rest — an
   * edit form filled from it would save blanks over a trading name, a legal
   * form and a year it had never been shown.
   */
  async profile(id: string | number, { signal }: ServiceOptions = {}): Promise<ClientProfile> {
    if (!LIVE()) {
      /* The fixture rows are the mockup's, in several shapes. */
      const row = clients.rows.find((c) => String(c.id) === String(id)) as
        | Record<string, any>
        | undefined
      if (!row) throw new Error('Client not found')
      return apiResolve(
        {
          ...EMPTY_PROFILE,
          id: row.id,
          name: row.name ?? '',
          industry: row.industry ?? null,
          contact_email: row.primaryContactEmail ?? null,
          primary_contact_name: row.primaryContactName ?? null,
          country: row.country || null,
          trademark_count: row.trademarks ?? 0,
          open_case_count: row.activeCases ?? 0,
        },
        { signal, path: `/clients/${id}` }
      )
    }
    return http.get(`/clients/${id}`, { signal })
  },

  async create(payload: Filters, { signal }: ServiceOptions = {}): Promise<Client> {
    if (!LIVE()) {
      const echoed = await apiEcho(payload, {
        id: `CLI-${Date.now()}`,
        signal,
        path: '/clients',
        method: 'POST',
      })
      return createClient(echoed)
    }
    return clientFromApi(await http.post('/clients', payload, { signal }))
  },

  async update(id: string | number, payload: Filters, { signal }: ServiceOptions = {}) {
    if (!LIVE()) {
      const echoed = await apiEcho(payload, { id, signal, path: `/clients/${id}`, method: 'PATCH' })
      return createClient(echoed)
    }
    return clientFromApi(await http.patch(`/clients/${id}`, payload, { signal }))
  },

  /**
   * The brands belonging to one client, for the dossier's portfolio section.
   *
   * Deliberately not routed through `trademarks.service`: that whole surface
   * is wired in W3.M7, and half-converting it here would leave two owners of
   * the same seam. This asks the brands endpoint the one question the client
   * dossier needs and no more.
   */
  async brandsFor(
    clientId: string | number,
    { signal }: ServiceOptions = {}
  ): Promise<ClientBrand[]> {
    if (!LIVE()) return apiResolve([], { signal, path: '/brands' })
    const page: Paged<ClientBrand> = await http.get('/brands', {
      params: { client_id: clientId, limit: 100 },
      signal,
    })
    return page.items ?? []
  },

  async remove(id: string | number, { signal }: ServiceOptions = {}): Promise<void> {
    if (!LIVE()) return apiResolve(undefined as never, { signal, path: `/clients/${id}` })
    await http.delete(`/clients/${id}`, { signal })
  },
}
